import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { normaliseState } from '@pulsepoint/shared';
import { Building2, Check, MapPin, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useDeactivateOrganisation, useOrganisations, useUpdateOrganisation, type Organisation } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { OrganisationDialog } from './OrganisationDialog';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Settings, Organisations: the businesses Pulsepoint serves. */
export function OrganisationsTab() {
  const canCreate = usePermission('organisations.create');
  const canDeactivate = usePermission('organisations.delete');
  const canEdit = usePermission('organisations.edit');
  const [showInactive, setShowInactive] = useState(false);
  const orgs = useOrganisations(canDeactivate ? 'all' : 'active');
  const dialog = useDialog();
  const toast = useToast();
  const deactivate = useDeactivateOrganisation();
  const update = useUpdateOrganisation();

  const rows = (orgs.data ?? []).filter((o) => showInactive || o.isActive);
  const inactiveCount = (orgs.data ?? []).filter((o) => !o.isActive).length;
  const open = (o?: Organisation, initialTab?: 'sites') => void dialog.open(OrganisationDialog, { organisation: o, initialTab });

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
    }
  };

  const columns: Column<Organisation>[] = [
    {
      key: 'name',
      header: 'Organisation',
      sortValue: (o) => o.businessName.toLowerCase(),
      render: (o) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{o.businessName}</Typography>
          <Typography variant="body2" color="textSecondary">
            {[o.tradingAs && o.tradingAs !== o.businessName ? `Trading as ${o.tradingAs}` : null, o.accountNumber].filter(Boolean).join(' · ') || ' '}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      hideBelow: 'md',
      sortValue: (o) => o.contactName?.toLowerCase() ?? null,
      render: (o) =>
        o.contactName || o.contactEmail ? (
          <Box>
            <Typography>{o.contactName ?? ''}</Typography>
            <Typography variant="body2" color="textSecondary">{o.contactEmail ?? o.contactPhone ?? ''}</Typography>
          </Box>
        ) : (
          <Typography color="textSecondary">—</Typography>
        ),
    },
    { key: 'where', header: 'Location', hideBelow: 'md', sortValue: (o) => o.addressSuburb?.toLowerCase() ?? null, render: (o) => [o.addressSuburb, normaliseState(o.addressState) ?? o.addressState].filter(Boolean).join(' ') || '—' },
    {
      key: 'powersensor',
      header: 'Powersensor',
      align: 'right',
      hideBelow: 'sm',
      sortValue: (o) => (o.powersensorEnabled ? 0 : 1),
      render: (o) => (o.powersensorEnabled ? <Check size={18} strokeWidth={2} aria-label="Powersensor on" style={{ verticalAlign: 'middle' }} /> : null),
    },
    { key: 'chargers', header: 'Chargers', align: 'right', sortValue: (o) => o.chargerCount, render: (o) => <span className="num">{o.chargerCount}</span> },
    { key: 'drivers', header: 'Drivers', align: 'right', hideBelow: 'sm', sortValue: (o) => o.driverCount, render: (o) => <span className="num">{o.driverCount}</span> },
    { key: 'sites', header: 'Sites', align: 'right', sortValue: (o) => o.siteCount, render: (o) => <span className="num">{o.siteCount}</span> },
    ...(showInactive
      ? [{ key: 'status', header: 'Status', render: (o: Organisation) => (o.isActive ? <StatusLabel tone="success">Active</StatusLabel> : <StatusLabel tone="off">Deactivated</StatusLabel>) }]
      : []),
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' }, mb: 3 }}>
        <Box>
          <Typography variant="h2">Organisations.</Typography>
          <Typography color="textSecondary" sx={{ mt: 0.5 }}>
            {canCreate ? 'The businesses, strata and fleets that host chargers, and their sites.' : 'Your organisation details and sites.'}
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => open()}>
            Add an organisation
          </Button>
        )}
      </Stack>
      <DataTable
        label="Organisations"
        rows={rows}
        columns={columns}
        rowKey={(o) => o.id}
        initialSort={{ key: 'name', direction: 'asc' }}
        searchText={(o) => [o.businessName, o.tradingAs, o.accountNumber, o.contactName, o.contactEmail, o.addressSuburb].filter(Boolean).join(' ')}
        searchPlaceholder="Search by name, account or contact"
        toolbar={
          inactiveCount > 0 && (
            <FormControlLabel control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />} label={`Show deactivated (${inactiveCount})`} />
          )
        }
        onRowClick={(o) => open(o)}
        rowActions={(o) => (
          <RowMenu
            label={`Actions for ${o.businessName}`}
            items={[
              { label: canEdit ? 'Edit' : 'View', icon: Pencil, onClick: () => open(o) },
              { label: 'Sites', icon: MapPin, onClick: () => open(o, 'sites') },
              {
                label: 'Reactivate',
                icon: RotateCcw,
                hidden: !canDeactivate || o.isActive,
                onClick: () => void run(() => update.mutateAsync({ id: o.id, isActive: true }), `${o.businessName} is active again.`),
              },
              {
                label: 'Deactivate',
                icon: Building2,
                danger: true,
                hidden: !canDeactivate || !o.isActive,
                onClick: async () => {
                  const ok = await dialog.confirm({
                    title: `Deactivate ${o.businessName}?`,
                    message: (
                      <>
                        It disappears from lists and pickers. Its history stays, and you can reactivate it later.
                        {o.chargerCount > 0 && <> Its {plural(o.chargerCount, 'charger')} keep working until you move or remove them.</>}
                      </>
                    ),
                    confirmLabel: 'Deactivate',
                    danger: true,
                  });
                  if (ok) await run(() => deactivate.mutateAsync(o.id), `${o.businessName} was deactivated.`);
                },
              },
            ]}
          />
        )}
        loading={orgs.isPending}
        error={orgs.error}
        onRetry={() => void orgs.refetch()}
        empty={
          <EmptyState title="No organisations yet" action={canCreate && <Button onClick={() => open()}>Add the first organisation</Button>}>
            An organisation is a business, strata or fleet that hosts chargers.
          </EmptyState>
        }
      />
    </Box>
  );
}
